const COMBINED_TAG_REGEX = /\[\[([^\]]+)\]\]|\{\{([^}]+)\}\}|%%([^%]+)%%|@@([^@]+)@@/g;

export function shuffleArray(items) {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function parseStarMarkedParts(parts) {
  const options = [];
  let correct = '';

  parts.forEach((part) => {
    if (part.startsWith('*')) {
      correct = part.slice(1);
      options.push(correct);
    } else {
      options.push(part);
    }
  });

  if (!correct && options.length > 0) {
    correct = options[options.length - 1];
  }

  return { options, correct };
}

function parseDropdownTag(rawTag) {
  const parts = rawTag.split('|').map((part) => part.trim()).filter(Boolean);
  const { options, correct } = parseStarMarkedParts(parts);
  const uniqueOptions = [...new Set(options.filter(Boolean))];

  return {
    type: 'gap',
    options: shuffleArray(uniqueOptions.length ? uniqueOptions : [correct]),
    correct,
  };
}

function parseInputTag(rawTag) {
  const parts = rawTag.split('|').map((part) => part.trim()).filter(Boolean);
  const { options, correct } = parseStarMarkedParts(parts);
  const hintParts = options.filter((option) => option !== correct);

  return {
    type: 'input',
    hint: hintParts.join(' / ') || correct,
    correct,
  };
}

function parseOrderTag(rawTag) {
  const words = rawTag.split('|').map((part) => part.trim()).filter(Boolean);
  const tokens = words.map((text, index) => ({
    id: String(index),
    text,
  }));

  return {
    type: 'order',
    tokens,
    correctOrder: tokens.map((token) => token.id),
  };
}

function parseCheckboxTag(rawTag) {
  const parts = rawTag.split('|').map((part) => part.trim()).filter(Boolean);
  const options = parts.map((part, index) => {
    const isCorrect = part.startsWith('*');
    return {
      id: String(index),
      text: isCorrect ? part.slice(1) : part,
      correct: isCorrect,
    };
  });

  return {
    type: 'checkbox',
    options: shuffleArray(options),
  };
}

export function parseMaterialTags(raw) {
  if (!raw) return [];

  const segments = [];
  let lastIndex = 0;
  let match;

  while ((match = COMBINED_TAG_REGEX.exec(raw)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: raw.slice(lastIndex, match.index) });
    }

    if (match[1] !== undefined) {
      segments.push(parseDropdownTag(match[1]));
    } else if (match[2] !== undefined) {
      segments.push(parseInputTag(match[2]));
    } else if (match[3] !== undefined) {
      segments.push(parseOrderTag(match[3]));
    } else if (match[4] !== undefined) {
      segments.push(parseCheckboxTag(match[4]));
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < raw.length) {
    segments.push({ type: 'text', value: raw.slice(lastIndex) });
  }

  return segments;
}

export function buildTagFromSelection(selectedWord, wrongOptionsText) {
  const wrongs = wrongOptionsText
    ? wrongOptionsText.split(',').map((item) => item.trim()).filter(Boolean)
    : [];
  return `[[${[...wrongs, `*${selectedWord}`].join('|')}]]`;
}

export function buildInputTagFromSelection(hint, correctAnswer) {
  return `{{${hint}|*${correctAnswer}}}`;
}

export function buildOrderTagFromSelection(sentence) {
  const words = sentence.trim().split(/\s+/).filter(Boolean);
  return `%%${words.join('|')}%%`;
}

export function buildCheckboxTagFromLines(linesText) {
  const lines = linesText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return `@@${lines.join('|')}@@`;
}

export function parseMaterialFileContent(fileText, fileName = '') {
  let title = fileName.replace(/\.txt$/i, '').trim();
  let rawText = fileText.replace(/^\uFEFF/, '');

  const lines = rawText.split('\n');
  const firstLine = lines[0]?.trim() || '';

  const hashTitle = firstLine.match(/^#\s+(.+)$/);
  const labelTitle = firstLine.match(/^title:\s*(.+)$/i);

  if (hashTitle) {
    title = hashTitle[1].trim();
    rawText = lines.slice(1).join('\n').replace(/^\s+/, '');
  } else if (labelTitle) {
    title = labelTitle[1].trim();
    rawText = lines.slice(1).join('\n').replace(/^\s+/, '');
  }

  return { title, rawText: rawText.trim() };
}

export function createEmptyMaterialProgress() {
  return {
    gaps: {},
    inputs: {},
    orders: {},
    checkboxes: {},
  };
}

export function normalizeMaterialProgress(progress) {
  const base = createEmptyMaterialProgress();
  if (!progress || typeof progress !== 'object') return base;

  return {
    gaps: progress.gaps && typeof progress.gaps === 'object' ? progress.gaps : {},
    inputs: progress.inputs && typeof progress.inputs === 'object' ? progress.inputs : {},
    orders: progress.orders && typeof progress.orders === 'object' ? progress.orders : {},
    checkboxes: progress.checkboxes && typeof progress.checkboxes === 'object' ? progress.checkboxes : {},
  };
}

export function materialProgressHasAnswers(progress) {
  if (!progress) return false;
  const hasGaps = Object.values(progress.gaps || {}).some((item) => item?.value);
  const hasInputs = Object.values(progress.inputs || {}).some((item) => item?.value);
  const hasOrders = Object.values(progress.orders || {}).some(
    (item) => (item?.answerIds || []).length > 0
  );
  const hasCheckboxes = Object.values(progress.checkboxes || {}).some(
    (item) => (item?.selectedIds || []).length > 0
  );
  return hasGaps || hasInputs || hasOrders || hasCheckboxes;
}

export function seededShuffle(items, seedString) {
  let seed = 0;
  for (let i = 0; i < seedString.length; i += 1) {
    seed = ((seed << 5) - seed + seedString.charCodeAt(i)) | 0;
  }
  seed = Math.abs(seed) || 1;

  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    seed = (seed * 9301 + 49297) % 233280;
    const j = Math.floor((seed / 233280) * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function normalizeMaterialAnswer(value) {
  return value.trim().toLowerCase();
}

export function isMaterialAnswerCorrect(value, correct) {
  return normalizeMaterialAnswer(value) === normalizeMaterialAnswer(correct);
}

export function playSuccessSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(523.25, ctx.currentTime);
    osc.frequency.setValueAtTime(659.25, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.35);
  } catch {
    // ignore audio errors
  }
}

export function playFailureSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(220, ctx.currentTime);
    osc.frequency.setValueAtTime(180, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.3);
  } catch {
    // ignore audio errors
  }
}
